<template>
  <div 
    v-if="evaluation && recommendation" 
    class="border rounded-xl shadow-sm p-6"
    :class="{
      'bg-white border-gray-200': approved === null || approved === undefined,
      'bg-green-50 border-green-300': approved === true,
      'bg-red-50 border-red-300': approved === false
    }"
  >
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-xl font-semibold text-gray-800">
        AI Generated Evaluation
      </h3>
      <div class="flex items-center gap-2">
        <span v-if="approved === true" class="px-3 py-1 text-sm rounded-full bg-green-100 text-green-800">
          ✓ Approved and Saved
        </span>
        <span v-else-if="approved === false" class="px-3 py-1 text-sm rounded-full bg-red-100 text-red-800">
          ✗ Declined
        </span>
        <span class="px-3 py-1 text-sm rounded-full bg-purple-100 text-purple-800">
          {{ pipelineUsed === 'RAG' ? '🔍 RAG Mode' : '🤖 Direct LLM' }}
        </span>
      </div>
    </div>

    <p class="text-sm text-gray-600 mb-4">
      Review the AI-generated evaluation and recommendation below. You can accept to save it or decline to record it as not suitable.
    </p>

    <!-- Input Data Section (Expandable) -->
    <div v-if="userHistory || teamHistory" class="mb-4">
      <button
        @click="showInputData = !showInputData"
        class="flex items-center justify-between w-full p-2 bg-gray-100 hover:bg-gray-200 rounded text-sm text-gray-700 transition-colors"
      >
        <span class="flex items-center gap-2">
          <Squares2X2Icon class="w-4 h-4" />
          <span class="font-semibold">Show Input Data (Criteria & Comments)</span>
          <span v-if="generationStartedAt" class="font-bold text-gray-900">
            — {{ formatTimestamp(generationStartedAt) }}
          </span>
        </span>
        <ChevronDownIcon
          class="w-5 h-5 transition-transform"
          :class="{ 'rotate-180': showInputData }"
        />
      </button>
      
      <div v-show="showInputData" class="mt-2 p-4 bg-gray-50 rounded border border-gray-200 space-y-4">
        <!-- Comment List -->
        <div v-if="formatCommentList().length > 0">
          <h4 class="text-sm font-bold text-gray-700 mb-2">Comment List:</h4>
          <div class="bg-white p-3 rounded border border-gray-300 text-xs font-mono space-y-1">
            <div v-for="(comment, idx) in formatCommentList()" :key="idx" class="text-gray-800">
              {{ comment }}
            </div>
          </div>
        </div>

        <!-- User Criteria Value Table -->
        <div v-if="formatUserCriteriaTable().length > 0">
          <h4 class="text-sm font-bold text-gray-700 mb-2">User Criteria Value Table (only user data with role average):</h4>
          <div class="bg-white p-3 rounded border border-gray-300 overflow-x-auto">
            <table class="w-full text-xs font-mono">
              <thead>
                <tr class="border-b border-gray-300">
                  <th class="text-left py-1 pr-4">Criterion</th>
                  <th class="text-center py-1 px-2">Value</th>
                  <th class="text-center py-1 pl-2">Average</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in formatUserCriteriaTable()" :key="idx" class="border-t border-gray-200">
                  <td class="py-1 pr-4">{{ row.name }}</td>
                  <td class="text-center py-1 px-2" :class="{
                    'text-green-700 font-semibold': row.value >= row.avg,
                    'text-orange-700 font-semibold': row.value < row.avg
                  }">{{ row.value }}</td>
                  <td class="text-center py-1 pl-2 text-gray-600">{{ row.avg }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Team Criteria Value Table -->
        <div v-if="formatTeamCriteriaTable().length > 0">
          <h4 class="text-sm font-bold text-gray-700 mb-2">Team Criteria Value Table (collective view of user's teams):</h4>
          <div class="bg-white p-3 rounded border border-gray-300 overflow-x-auto">
            <table class="w-full text-xs font-mono">
              <thead>
                <tr class="border-b border-gray-300">
                  <th class="text-left py-1 pr-4">Criterion</th>
                  <th class="text-center py-1 px-2">Value</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in formatTeamCriteriaTable()" :key="idx" class="border-t border-gray-200">
                  <td class="py-1 pr-4">{{ row.name }}</td>
                  <td class="text-center py-1 px-2 text-gray-800">{{ row.value }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="formatCommentList().length === 0 && formatUserCriteriaTable().length === 0 && formatTeamCriteriaTable().length === 0" class="text-center py-4 text-gray-500 text-sm">
          No input data available
        </div>
      </div>
    </div>

    <!-- Performance Timings (collapsible) -->
    <div v-if="performanceTimings" class="mb-4">
      <button
        @click="showTimings = !showTimings"
        class="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
      >
        <ChevronDownIcon v-if="!showTimings" class="w-4 h-4" />
        <ChevronUpIcon v-else class="w-4 h-4" />
        Performance Details
      </button>
      <div v-if="showTimings" class="mt-2 p-3 bg-gray-50 rounded text-xs">
        <div class="grid grid-cols-2 gap-2">
          <div>Total Time: <strong>{{ performanceTimings.total_time?.toFixed(2) }}s</strong></div>
          <div>LLM Generation: <strong>{{ performanceTimings.llm_generation?.toFixed(2) }}s</strong></div>
          <div>User Criteria: {{ performanceTimings.user_criteria_query?.toFixed(3) }}s</div>
          <div>Team Criteria: {{ performanceTimings.team_criteria_query?.toFixed(3) }}s</div>
        </div>
      </div>
    </div>

    <!-- Individual Feedback Section -->
    <div class="mb-3">
      <label class="block font-semibold text-gray-700 mb-1 text-sm">Individual Feedback</label>
      <textarea
        v-if="isEditing"
        v-model="editedIndividualEvaluation"
        class="w-full bg-white p-3 rounded border border-blue-300 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[120px]"
        rows="6"
      ></textarea>
      <div v-else class="bg-white p-3 rounded border border-gray-200 text-sm text-gray-700 whitespace-pre-wrap">
        {{ individualEvaluation }}
      </div>
    </div>

    <!-- Team Feedback Section -->
    <div v-if="teamEvaluation || isEditing" class="mb-3">
      <label class="block font-semibold text-gray-700 mb-1 text-sm flex items-center">
        <UserGroupIcon class="w-4 h-4 mr-1" />
        Team Feedback
      </label>
      <textarea
        v-if="isEditing"
        v-model="editedTeamEvaluation"
        class="w-full bg-green-50 p-3 rounded border border-green-300 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 min-h-[80px]"
        rows="4"
        placeholder="Team-specific feedback (optional)"
      ></textarea>
      <div v-else class="bg-green-50 p-3 rounded border border-green-200 text-sm text-gray-700 whitespace-pre-wrap">
        <span class="font-semibold text-green-800">TEAM:</span> {{ teamEvaluation }}
      </div>
    </div>

    <!-- Recommendation Section -->
    <div class="mb-3">
      <label class="block font-semibold text-gray-700 mb-1 text-sm">Recommendation</label>
      <textarea
        v-if="isEditing"
        v-model="editedRecommendation"
        class="w-full bg-white p-3 rounded border border-blue-300 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[120px]"
        rows="6"
      ></textarea>
      <div v-else class="bg-white p-3 rounded border border-gray-200 text-sm text-gray-700 whitespace-pre-wrap">
        {{ recommendation }}
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="flex justify-end gap-3">
      <BaseButton
        v-if="!isEditing"
        variant="edit"
        @click="toggleEdit"
        :disabled="isProcessing"
      >
        Edit
      </BaseButton>
      <BaseButton
        v-if="isEditing"
        variant="primary"
        @click="saveEdit"
        :disabled="isProcessing"
      >
        Save
      </BaseButton>
      <BaseButton
        v-if="isEditing"
        variant="cancel"
        @click="cancelEdit"
        :disabled="isProcessing"
      >
        Cancel
      </BaseButton>
      <BaseButton
        v-if="!isEditing"
        variant="cancel"
        @click="handleClear"
        :disabled="isProcessing"
      >
        <TrashIcon v-if="!isProcessing" class="h-5 w-5 mr-1" />
        <svg v-else class="animate-spin h-5 w-5 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span v-if="!isProcessing">Clear</span>
        <span v-else>Processing...</span>
      </BaseButton>
      <BaseButton
        v-if="approved !== false && !isEditing"
        variant="delete"
        @click="handleDecline"
        :disabled="isProcessing"
      >
        <XMarkIcon v-if="!isProcessing" class="h-5 w-5 mr-1" />
        <svg v-else class="animate-spin h-5 w-5 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span v-if="!isProcessing">Decline</span>
        <span v-else>Processing...</span>
      </BaseButton>
      <BaseButton
        v-if="approved !== true && !isEditing"
        variant="primary"
        @click="handleAccept"
        :disabled="isProcessing"
      >
        <CheckIcon v-if="!isProcessing" class="h-5 w-5 mr-1" />
        <svg v-else class="animate-spin h-5 w-5 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span v-if="!isProcessing">Approve</span>
        <span v-else>Processing...</span>
      </BaseButton>
    </div>

    <!-- Approval Status -->
    <div v-if="approved !== null" class="mt-3 pt-3 border-t border-gray-300">
      <p class="text-sm font-semibold" :class="{
        'text-green-700': approved === true,
        'text-red-700': approved === false
      }">
        {{ approved ? '✓ Approved and Saved' : '✗ Declined' }}
      </p>
    </div>
  </div>
</template>

<script>
import BaseButton from "@/BaseComponents/BaseButton.vue";
import { CheckIcon, XMarkIcon, TrashIcon, ChevronDownIcon, ChevronUpIcon, UserGroupIcon, Squares2X2Icon } from "@heroicons/vue/24/outline";

export default {
  name: 'AIEvaluationSection',
  components: { BaseButton, CheckIcon, XMarkIcon, TrashIcon, ChevronDownIcon, ChevronUpIcon, UserGroupIcon, Squares2X2Icon },
  props: {
    evaluation: {
      type: String,
      default: ''
    },
    recommendation: {
      type: String,
      default: ''
    },
    individualEvaluation: {
      type: String,
      default: ''
    },
    teamEvaluation: {
      type: String,
      default: ''
    },
    userHistoryCount: {
      type: Number,
      default: 0
    },
    teamHistoryCount: {
      type: Number,
      default: 0
    },
    pipelineUsed: {
      type: String,
      default: 'LLM'
    },
    performanceTimings: {
      type: Object,
      default: null
    },
    generationStartedAt: {
      type: String,
      default: null
    },
    userHistory: {
      type: Array,
      default: () => []
    },
    teamHistory: {
      type: Array,
      default: () => []
    },
    approved: {
      type: Boolean,
      default: null
    }
  },
  emits: ['accept', 'decline', 'clear', 'save-edit'],
  data() {
    return {
      showTimings: false,
      showInputData: false,
      isProcessing: false,
      isEditing: false,
      editedIndividualEvaluation: '',
      editedTeamEvaluation: '',
      editedRecommendation: ''
    };
  },
  methods: {
    handleAccept() {
      this.isProcessing = true;
      this.$emit('accept');
    },
    handleDecline() {
      this.isProcessing = true;
      this.$emit('decline');
    },
    handleClear() {
      this.isProcessing = true;
      this.$emit('clear');
    },
    toggleEdit() {
      this.isEditing = true;
      this.editedIndividualEvaluation = this.individualEvaluation || '';
      this.editedTeamEvaluation = this.teamEvaluation || '';
      this.editedRecommendation = this.recommendation || '';
    },
    saveEdit() {
      this.isEditing = false;
      // Emit the edited values back to parent
      this.$emit('save-edit', {
        individualEvaluation: this.editedIndividualEvaluation,
        teamEvaluation: this.editedTeamEvaluation,
        recommendation: this.editedRecommendation
      });
    },
    cancelEdit() {
      this.isEditing = false;
      this.editedIndividualEvaluation = this.individualEvaluation || '';
      this.editedTeamEvaluation = this.teamEvaluation || '';
      this.editedRecommendation = this.recommendation || '';
    },
    
    // Format comment list exactly as in the prompt with value changes
    formatCommentList() {
      const comments = [];
      
      // User history comments
      if (this.userHistory) {
        for (const entry of this.userHistory) {
          if (entry.text_values && entry.text_values.length > 0) {
            for (const textItem of entry.text_values) {
              // Handle both old format (string) and new format (object with text and value_change)
              let text = '';
              let valueChange = null;
              
              if (typeof textItem === 'string') {
                text = textItem;
              } else if (typeof textItem === 'object' && textItem.text) {
                text = textItem.text;
                valueChange = textItem.value_change;
              }
              
              // Format with value change if available
              if (valueChange !== null && valueChange !== 0) {
                const sign = valueChange > 0 ? '+' : '';
                comments.push(`- ${entry.criterion_name} (${sign}${valueChange}): ${text}`);
              } else {
                comments.push(`- ${entry.criterion_name}: ${text}`);
              }
            }
          }
        }
      }
      
      // Team history comments
      if (this.teamHistory) {
        for (const entry of this.teamHistory) {
          if (entry.text_values && entry.text_values.length > 0) {
            for (const textItem of entry.text_values) {
              // Handle both old format (string) and new format (object with text and value_change)
              let text = '';
              let valueChange = null;
              
              if (typeof textItem === 'string') {
                text = textItem;
              } else if (typeof textItem === 'object' && textItem.text) {
                text = textItem.text;
                valueChange = textItem.value_change;
              }
              
              const prefix = text.includes('TEAM:') ? '' : 'TEAM: ';
              
              // Format with value change if available
              if (valueChange !== null && valueChange !== 0) {
                const sign = valueChange > 0 ? '+' : '';
                comments.push(`- ${entry.criterion_name} (${sign}${valueChange}): ${prefix}${text}`);
              } else {
                comments.push(`- ${entry.criterion_name}: ${prefix}${text}`);
              }
            }
          }
        }
      }
      
      return comments;
    },
    
    // Format USER criteria table (only user data with role average)
    formatUserCriteriaTable() {
      const rows = [];
      
      if (this.userHistory) {
        for (const entry of this.userHistory) {
          if (entry.criterion_type === 'countable') {
            rows.push({
              name: entry.criterion_name,
              value: entry.int_value || 0,
              avg: entry.avg_value ? (typeof entry.avg_value === 'number' ? entry.avg_value.toFixed(1) : entry.avg_value) : '0.0'
            });
          }
        }
      }
      
      return rows;
    },
    
    // Format TEAM criteria table (only team data without average)
    formatTeamCriteriaTable() {
      const rows = [];
      
      if (this.teamHistory) {
        for (const entry of this.teamHistory) {
          if (entry.criterion_type === 'countable' && entry.int_value > 0) {
            rows.push({
              name: entry.criterion_name,
              value: entry.int_value || 0
            });
          }
        }
      }
      
      return rows;
    },
    
    // Format ISO timestamp to readable date/time
    formatTimestamp(isoString) {
      if (!isoString) return '';
      try {
        const date = new Date(isoString);
        return date.toLocaleString('de-DE', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      } catch (e) {
        return isoString;
      }
    }
  },
  watch: {
    evaluation() {
      this.isProcessing = false;
    }
  }
};
</script>
